import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { ExplorePresentationProps } from './ExplorePropertiesContainer';
import NawyLoadingIndicator from '../../../../../new-components/NawyLoadingIndicator';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useSearchContext } from '../../../../search-new/context/SearchContext';
import ErrorComponent from '../../../../../new-components/ui/ErrorComponent';
import ResetFilters from '../../components/explore-properties/ResetFilters';
import SaleTypeTabs from './SaleTypeTabs';
import FilterIcon from '../../../../../new-components/svg-icons/FilterIcon';
import { useSorting } from '../../../../search-new/hooks/Sorting.hooks';
import useExploreSearch from '../../../components/explore-properties/hooks/CompoundResults';
import PropertyCard from './PropertyCard';
import SortByContainer from './SortByContainer';
import LoadMoreContainer from '../../../../../new-components/load-more/LoadMoreContainer';

const ExplorePropertiesPresentation = ({
  headline,
  entityDetails,
  changePageHandler,
  explorePropertiesRef,
  exploreLocation,
  saleTypes,
  handleTabChange,
  appliedFilters,
  pageNumber,
  hasAvailability
}: ExplorePresentationProps) => {
  const { t } = useTranslation(['entity', 'common']);
  const router = useRouter();

  const { searchFiltersState, searchFiltersDispatch, setShowFilters } =
    useSearchContext();
  const [loadedData, setLoadedData] = useState<any>([]);
  const searchResults = useExploreSearch(
    entityDetails?.id!,
    pageNumber,
    exploreLocation
  );
  const { sorting } = useSorting();

  useEffect(() => {
    if (searchResults.loadedSearchResults) {
      if (pageNumber == 1 || pageNumber == undefined) {
        setLoadedData([...searchResults.loadedSearchResults]);
      } else {
        setLoadedData([...loadedData, ...searchResults.loadedSearchResults]);
      }
    }
  }, [searchResults.loadedSearchResults]);

  const getInfo = () => {
    return {
      results: searchResults.loadedSearchResults,
      properties_count: searchResults.loadedSearchResultsCount,
      compounds_count: searchResults.loadedSearchResultsCount,
      isLoading: searchResults.isLoading,
      isError: searchResults.isError
    };
  };

  const getLoadMoreContainer = () => {
    const { compounds_count, properties_count, isLoading } = getInfo();
    return (
      <LoadMoreContainer
        loadedDataLength={loadedData.length}
        limit={
          searchFiltersState.searchFilters.category === 'property'
            ? properties_count
            : compounds_count
        }
        isLoading={isLoading}
        pageNumber={pageNumber}
        onChange={() => changePageHandler && changePageHandler(pageNumber + 1)}
      />
    );
  };

  const getFilterBar = () => {
    return (
      <FilterBar>
        <SaleTypeTabs saleTypes={saleTypes} handleTabChange={handleTabChange} />
      </FilterBar>
    );
  };

  const getCardsContainer = () => {
    const { results, isLoading, isError } = getInfo();
    if (isError) return <ErrorComponent />;
    else if (isLoading && pageNumber == 1) {
      return <NawyLoadingIndicator />;
    } else if ((results && results?.length > 0) || loadedData?.length > 0) {
      return (
        <CardsContainer>
          {loadedData.map((result: any, index: number) => (
            <PropertyCard
              key={result.id || result.slug || index}
              property={result}
              cardPosition={index + 1}
            />
          ))}
        </CardsContainer>
      );
    } else {
      return <ResetFilters hasAvailability={hasAvailability} />;
    }
  };
  const getExploreHeadlineText = () => {
    const { isLoading, compounds_count, properties_count } = getInfo();
    if (headline) {
      return (
        <div className="results-title">
          <h2 className="entity-title">{t(headline)}</h2>
          {!isLoading ? (
            <span className="headline-subtitle">
              {properties_count || compounds_count} {t('results_available')}
            </span>
          ) : (
            <span></span>
          )}
        </div>
      );
    }

    return (
      <div className="results-title">
        <h2 className="entity-title">{t('properties', { ns: 'common' })}</h2>

        {!isLoading ? (
          <span className="headline-subtitle">
            {properties_count || compounds_count} {t('results_available')}
          </span>
        ) : (
          <span></span>
        )}
      </div>
    );
  };

  return (
    <ExploreContainer ref={explorePropertiesRef} className="explore-container">
      <div className="title">
        {getExploreHeadlineText()}
        <div className="title-actions">
          <FilterBtn onClick={() => setShowFilters(true)}>
            {t('filter')}
            <FilterIcon color="#015C9A" />
            {appliedFilters && appliedFilters > 0 ? (
              <span className="filters-indicator">{appliedFilters}</span>
            ) : null}
          </FilterBtn>

          <SortByContainer
            sortByOptions={sorting}
            searchFiltersDispatch={searchFiltersDispatch}
            searchFiltersState={searchFiltersState}
            sortLocation="explore_properties"
          />
        </div>
      </div>
      {getFilterBar()}

      <div className="cards-wrapper">{getCardsContainer()}</div>
      {getLoadMoreContainer()}
    </ExploreContainer>
  );
};

const ExploreContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;

  .title {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;

    .results-title {
      display: flex;
      max-width: calc(100% - 164px);
      gap: 4px;
      flex-direction: column;

      .entity-title {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        margin-bottom: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        color: #2c3138;
        font-family: materialMedium;
        font-size: 18px;
        text-transform: capitalize;
      }
      .headline-subtitle {
        font-size: 12px;
        color: #656565;
      }
    }
  }

  .title-actions {
    display: flex;
    gap: 16px;
    height: 40px;
  }

  .cards-wrapper {
    position: relative;
    min-height: 200px;
  }
`;

const FilterBar = styled.div`
  width: 100%;
  background-color: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9;
`;

const FilterBtn = styled.button`
  position: relative;
  background-color: transparent;
  outline: none;
  border: none;
  height: 100%;
  color: #015c9a;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-family: materialMedium;
  line-height: 150%;
  cursor: pointer;
  svg {
    width: 24px;
    height: 24px;
  }

  .filters-indicator {
    position: absolute;
    top: -2px;
    inset-inline-end: -4px;
    font-size: 12px;
    color: #fff;
    background-color: #ff3a00;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 32px;
  margin-top: 16px;
`;
export default ExplorePropertiesPresentation;
